import React, { useState, useEffect, useCallback, useRef } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { useLoading } from "../../../../context/LoadingContext";
import { jwtDecode } from "jwt-decode";
import { getParameterFromManager } from "../../../../services/Utils";
import "../../../../styles/general.css";
import "../../../../styles/amtpl.css";
import DataTable from "../../../../pages/Requirement/components/common/ReusableTable";
import SearchFilter from "../../../../pages/Requirement/components/common/SearchFilter";
import {
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  fetchData,
  searchfetchQueries,
  postQuery,
} from "../../../../pages/Requirement/services/RequirementServices";
import { v4 as uuidv4 } from "uuid";
import AlertModal from "../../../../pages/Requirement/components/common/AlerModel";

const AmtplBank = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [banks, setBanks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [searchValues, setSearchValues] = useState({
    searchBankName: "",
    searchEmail: "",
    searchAddress: "",
    searchCountry: "",
  });

  const [countrymaster, setCountryMaster] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editBankID, setEditBankID] = useState(null);
  const [selectedRow, setSelectedRow] = useState([]);

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const formRef = useRef(null);

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  // Form state
  const [formData, setFormData] = useState({
    bankName: "",
    email: "",
    address: "",
    country: "",
  });

  // State for errors
  const [errors, setErrors] = useState({});

  // Handle input change for the form
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Check required fields
    if (!formData.bankName) newErrors.bankName = "This field is required";
    if (!formData.email) newErrors.email = "This field is required";
    if (!formData.address) newErrors.address = "This field is required";
    if (!formData.country) newErrors.country = "This field is required";

    // Validate email format
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Simulate API call to save form data
  const saveFormData = async (data) => {
    setLoading(true);
    try {
      let body = null;
      console.log("Saving data to API:", data);
      if (isEditMode) {
        body = {
          target: "updateBankMaster",
          bankMasterInput: {
            BankID: data.BankID,
            BankName: data.bankName,
            Email: data.email,
            Address: data.address,
            Country: data.country,
          },
        };
      } else {
        body = {
          target: "createBankMaster",
          bankMasterInput: {
            BankID: data.BankID,
            BankName: data.bankName,
            Email: data.email,
            Address: data.address,
            Country: data.country,
          },
        };
      }

      console.log("Saving body to API:", body);
      if (body !== null) {
        const result = await postQuery(body);
        if (result) {
          setModal({
            show: true,
            type: "success",
            title: "Success",
            message: `Bank ${isEditMode ? "updated" : "added"} successfully!`,
            autoClose: false,
          });
        }
      }

      // Refresh the table data
      await searchBanks(searchValues, rowStart);
    } catch (error) {
      console.error("Error saving data:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: `Failed to ${isEditMode ? "update" : "add"} bank.`,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      let totalBankCount = 0;
      if (!isEditMode) {
        const [initialResponse] = await Promise.all([
          fetchData({
            target: "listBankMaster",
            userOrder: "BankName",
            rowStart: 0,
            pageSize: 1,
          }),
        ]);

        totalBankCount = initialResponse?.total || initialResponse?.length || 0;
      }

      const dataToSave = {
        ...formData,
        BankID: isEditMode ? editBankID : totalBankCount + 1,
      };
      await saveFormData(dataToSave);
      setShowForm(false);
      setIsEditMode(false);
      setEditBankID(null);
      setSelectedRow(null);
      setFormData({
        bankName: "",
        email: "",
        address: "",
        country: "",
      });
    } else {
      console.log("Validation failed");
    }
  };

  // Handle form reset
  const handleReset = () => {
    setFormData({
      bankName: "",
      email: "",
      address: "",
      country: "",
    });
    setErrors({});
    setIsEditMode(false);
    setEditBankID(null);
    setSelectedRow(null);
    setShowForm(false);
  };

  const loadCountries = useCallback(async () => {
    setLoading(true);
    try {
      const [allCountries] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 250,
          filterTerms: { Type: "Country" },
        }),
      ]);
      if (allCountries.items.length > 0) {
        setCountryMaster(allCountries.items);
      } else {
        setCountryMaster([]);
      }
    } catch (error) {
      console.error("Error loading countries: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const searchBanks = useCallback(
    async (searchValue, newRowStart = rowStart) => {
      setLoading(true);
      const body = {
        target: "listBankMaster",
        userOrder: "BankName",
        rowStart: newRowStart,
        pageSize: pageSize,
        filterTerms: {
          BankNamePartial: searchValue.searchBankName,
          EmailPartial: searchValue.searchEmail,
          AddressPartial: searchValue.searchAddress,
          Country: searchValue.searchCountry,
        },
      };
      try {
        const response = await fetchData(body);

        const modifiedItems = (response.items || response).map((item) => ({
          ...item,
          id: item.BankID, // Add id property with value from RequirementsID
        }));
        setBanks(modifiedItems);
        setTotalItems(response.total || 0);
      } catch (error) {
        console.error("Error loading listBanks: ", error);
        setBanks([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [rowStart, pageSize, setLoading]
  );

  const handleRowClick = useCallback(async (row) => {
    console.log("selected row::", row);
    setSelectedRow(row);
    setIsEditMode(true);
    setEditBankID(row.BankID);
    setShowForm(true);
  }, []);

  // Populate form data when selectedRow changes
  useEffect(() => {
    if (selectedRow && isEditMode) {
      setFormData({
        bankName: selectedRow.BankName || "",
        email: selectedRow.Email || "",
        address: selectedRow.Address || "",
        country: selectedRow.Country?.toString() || "",
      });
    }
  }, [selectedRow, isEditMode]);

  // Scroll to form when showForm is true
  useEffect(() => {
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [showForm]);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_ADMINS")) {
          await Auth.signOut();
          navigate("/");
        }
        await getParameterFromManager("INR_TO_USD");
      } catch (error) {
        await Auth.signOut();
        navigate("/");
      }
    };

    checkUser();
    loadCountries();
  }, [navigate, loadCountries]);

  useEffect(() => {
    searchBanks(searchValues);
  }, [searchBanks, searchValues]);

  const handlePageChange = (page) => {
    const newRowStart = (page - 1) * pageSize;
    setRowStart(newRowStart);
    searchBanks(searchValues, newRowStart);
  };

  const handleSearch = (searchTerm) => {
    console.log("Searching for:", searchTerm);
    searchBanks(searchTerm);
    setRowStart(0); // Reset to first page on new search
  };

  const currentPage = Math.floor(rowStart / pageSize) + 1;

  const fields = [
    {
      name: "searchBankName",
      label: "Bank Name",
      type: "text",
      value: searchValues.searchBankName,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchBankName: value }));
      },
    },
    {
      name: "searchEmail",
      label: "Email",
      type: "text",
      value: searchValues.searchEmail,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchEmail: value }));
      },
    },
    {
      name: "searchAddress",
      label: "Address",
      type: "text",
      value: searchValues.searchAddress,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchAddress: value }));
      },
    },
    {
      name: "searchCountry",
      label: "Country",
      type: "dropdown",
      options: countrymaster.map((item) => ({
        value: item.Value,
        label: item.Value,
      })),
      value: searchValues.searchCountry,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchCountry: value }));
      },
    },
  ];

  const columns = [
    {
      id: "BankName",
      numeric: false,
      label: "Bank Name",
    },
    {
      id: "Email",
      numeric: false,
      label: "Email",
    },
    {
      id: "Address",
      numeric: false,
      label: "Address",
    },
    {
      id: "Country",
      numeric: false,
      label: "Country",
    },
  ];
  return (
    <div className="amtpl-dashboard">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />

      <div className="content">
        <main className="main-content">
          <div className="search-filter4">
            <div className="title4">
              <div className="head-text">Bank Master</div>
            </div>
            <div className="filter4">
              <SearchFilter fields={fields} onSearch={handleSearch} />
            </div>
            <div
              className="head-btn"
              style={{ display: "flex", gap: "10px", alignItems: "center" }}
            >
              <div
                className="submit-btn"
                onClick={() => {
                  setShowForm(!showForm);
                  setIsEditMode(false);
                  setFormData({
                    bankName: "",
                    email: "",
                    address: "",
                    country: "",
                  });
                }}
              >
                Add
              </div>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={banks}
            total={totalItems}
            title="Bank Information"
            rowsPerPage={pageSize}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
            currentPage={currentPage}
            selectedRows={selectedRow ? [selectedRow.BankID || selectedRow.id] : []}
          />

          {showForm && (
            <div className="box-main">
              <div className="table-header">
                <div className="header-text">
                  <div className="head-text">
                    {/* {isEditMode ? "Edit" : "Add"}  */}
                  </div>
                </div>
                <div className="head-btn">
                  <>
                    <div onClick={handleSubmit} className="submit-btn">
                      {isEditMode ? "Update" : "Submit"}
                    </div>
                    <div onClick={handleReset} className="submit-btn">
                      {isEditMode ? "Cancel" : "Reset"}
                    </div>
                  </>
                </div>
              </div>

              {/* Form Section */}

              <form
                ref={formRef}
                onSubmit={handleSubmit}
                style={{
                  padding: "10px",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "8px",
                }}
              >
                <div className="row">
                  {/* Bank Name */}
                  <div className="col-md-3">
                    <div className="mt-3">
                      <Typography variant="body2">Bank Name</Typography>
                      <TextField
                        type="text"
                        name="bankName"
                        value={formData.bankName}
                        onChange={handleChange}
                        sx={{ backgroundColor: "white", width: "100%" }}
                        error={!!errors.bankName}
                        helperText={errors.bankName}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="col-md-3">
                    <div className="mt-3">
                      <Typography variant="body2">Email</Typography>
                      <TextField
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        sx={{ backgroundColor: "white", width: "100%" }}
                        error={!!errors.email}
                        helperText={errors.email}
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div className="col-md-3">
                    <div className="mt-3">
                      <Typography variant="body2">Address</Typography>
                      <TextField
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        sx={{ backgroundColor: "white", width: "100%" }}
                        error={!!errors.address}
                        helperText={errors.address}
                      />
                    </div>
                  </div>

                  {/* Country */}
                  <div className="col-md-3">
                    <div className="mt-3">
                      <FormControl
                        sx={{ minWidth: "100%" }}
                        error={!!errors.country}
                      >
                        <Typography variant="body2">Select Country</Typography>
                        <Select
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          displayEmpty
                          sx={{ backgroundColor: "white" }}
                          inputProps={{ "aria-label": "Select Country" }}
                        >
                          <MenuItem value="" disabled>
                            Select Country
                          </MenuItem>
                          {countrymaster.map((p) => (
                            <MenuItem key={p.ID} value={p.Value}>
                              {p.Value}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.country && (
                          <Typography variant="caption" color="error">
                            {errors.country}
                          </Typography>
                        )}
                      </FormControl>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};
export default AmtplBank;
